<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useRouter } from 'vue-router';

import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const showAddModal = ref(false);
const showDeleteModal = ref(false);
const selectedKb = ref(null);
const newKbName = ref('');
const newKbDescription = ref('');
const isSubmitting = ref(false);

const accountId = computed(() => store.getters.getCurrentAccountId);
const knowledgeBases = computed(() => store.getters['knowledgeBases/getKnowledgeBases']);
const uiFlags = computed(() => store.getters['knowledgeBases/getUIFlags']);

const openSettings = kb => {
  router.push(`/app/accounts/${accountId.value}/settings/knowledge-bases/${kb.id}`);
};

const openDeleteModal = kb => {
  selectedKb.value = kb;
  showDeleteModal.value = true;
};

const confirmDelete = async () => {
  try {
    await store.dispatch('knowledgeBases/delete', {
      accountId: accountId.value,
      id: selectedKb.value.id,
    });
    useAlert(t('KNOWLEDGE_BASE.LIST.DELETE.SUCCESS'));
  } catch {
    useAlert(t('KNOWLEDGE_BASE.LIST.DELETE.ERROR'));
  } finally {
    showDeleteModal.value = false;
    selectedKb.value = null;
  }
};

const createKnowledgeBase = async () => {
  if (!newKbName.value.trim()) return;
  isSubmitting.value = true;
  try {
    const kb = await store.dispatch('knowledgeBases/create', {
      accountId: accountId.value,
      data: { name: newKbName.value.trim(), description: newKbDescription.value.trim() },
    });
    useAlert(t('KNOWLEDGE_BASE.LIST.CREATE.SUCCESS'));
    showAddModal.value = false;
    newKbName.value = '';
    newKbDescription.value = '';
    openSettings(kb);
  } catch {
    useAlert(t('KNOWLEDGE_BASE.LIST.CREATE.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const statusBadgeClass = status => {
  return status === 'active'
    ? 'bg-green-100 text-green-700'
    : 'bg-n-alpha-2 text-n-slate-11';
};

onBeforeMount(() => {
  store.dispatch('knowledgeBases/get', { accountId: accountId.value });
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('KNOWLEDGE_BASE.LOADING')"
    :no-records-found="!knowledgeBases.length"
    :no-records-message="$t('KNOWLEDGE_BASE.LIST.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('KNOWLEDGE_BASE.TITLE')"
        :description="$t('KNOWLEDGE_BASE.DESCRIPTION')"
        feature-name="knowledge_bases"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('KNOWLEDGE_BASE.LIST.NEW_BUTTON')"
            @click="showAddModal = true"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <tr>
            <th class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11">
              {{ $t('KNOWLEDGE_BASE.LIST.TABLE.NAME') }}
            </th>
            <th class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11">
              {{ $t('KNOWLEDGE_BASE.LIST.TABLE.SOURCES') }}
            </th>
            <th class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11">
              {{ $t('KNOWLEDGE_BASE.LIST.TABLE.STATUS') }}
            </th>
            <th class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11" />
          </tr>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="kb in knowledgeBases" :key="kb.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="font-medium text-n-slate-12">{{ kb.name }}</span>
              <p v-if="kb.description" class="text-xs text-n-slate-11 mt-0.5 m-0">
                {{ kb.description }}
              </p>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 text-n-slate-11">
              {{ kb.sources_count || 0 }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span
                class="px-2 py-0.5 rounded text-xs font-medium"
                :class="statusBadgeClass(kb.status)"
              >
                {{ $t(`KNOWLEDGE_BASE.STATUS.${kb.status?.toUpperCase()}`) }}
              </span>
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-tooltip.top="$t('KNOWLEDGE_BASE.LIST.TABLE.CONFIGURE')"
                  icon="i-lucide-settings-2"
                  slate
                  xs
                  faded
                  @click="openSettings(kb)"
                />
                <Button
                  v-tooltip.top="$t('KNOWLEDGE_BASE.LIST.TABLE.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  @click="openDeleteModal(kb)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </SettingsLayout>

  <!-- Modal: Nova Base de Conhecimento -->
  <woot-modal v-model:show="showAddModal" :on-close="() => (showAddModal = false)">
    <div class="flex flex-col gap-4 p-6">
      <h3 class="text-lg font-semibold text-n-slate-12 m-0">
        {{ $t('KNOWLEDGE_BASE.LIST.CREATE.TITLE') }}
      </h3>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-11">
          {{ $t('KNOWLEDGE_BASE.FORM.NAME') }} *
        </label>
        <input
          v-model="newKbName"
          type="text"
          class="input"
          :placeholder="$t('KNOWLEDGE_BASE.FORM.NAME_PLACEHOLDER')"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-11">
          {{ $t('KNOWLEDGE_BASE.FORM.DESCRIPTION') }}
        </label>
        <textarea
          v-model="newKbDescription"
          class="input"
          rows="3"
          :placeholder="$t('KNOWLEDGE_BASE.FORM.DESCRIPTION_PLACEHOLDER')"
        />
      </div>
      <div class="flex justify-end gap-2 mt-2">
        <Button
          variant="ghost"
          :label="$t('KNOWLEDGE_BASE.FORM.CANCEL')"
          @click="showAddModal = false"
        />
        <Button
          :label="$t('KNOWLEDGE_BASE.FORM.CREATE')"
          :is-loading="isSubmitting"
          :disabled="!newKbName.trim()"
          @click="createKnowledgeBase"
        />
      </div>
    </div>
  </woot-modal>

  <!-- Modal: Confirmar exclusão -->
  <woot-delete-modal
    v-model:show="showDeleteModal"
    :on-close="() => (showDeleteModal = false)"
    :on-confirm="confirmDelete"
    :title="$t('KNOWLEDGE_BASE.LIST.DELETE.TITLE')"
    :message="$t('KNOWLEDGE_BASE.LIST.DELETE.MESSAGE')"
    :message-value="selectedKb?.name"
    :confirm-text="$t('KNOWLEDGE_BASE.LIST.DELETE.CONFIRM')"
    :reject-text="$t('KNOWLEDGE_BASE.LIST.DELETE.CANCEL')"
  />
</template>
